import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableRow, TableCell, CircularProgress, Alert, Button, Link } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';
import SettingsErrorAlert from './SettingsErrorAlert';

const GeneralSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/settings/general');
      setData(response.data.data);
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load general information',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;
  if (error) return (
    <Box>
      <SettingsErrorAlert message={error.message} path={error.path} status={error.status} />
      <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => { setError(null); load(); }}>Retry</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>General Information</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Default Provisioning Source & Destination</Typography>
        <Table size="small">
          <TableBody>
            <TableRow><TableCell>Default Source Type</TableCell><TableCell>{data?.source_configuration?.active_source_type || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Source Location</TableCell><TableCell>{data?.source_configuration?.base_local?.location || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Source Version</TableCell><TableCell>{data?.source_configuration?.base_local?.version || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Port Strategy Location</TableCell><TableCell>{data?.source_configuration?.port_strategy?.location || '-'}</TableCell></TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Port Strategy</Typography>
        {data?.port_strategy?.is_loaded ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Loaded from: {data?.port_strategy?.resolved_path}
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {data?.port_strategy?.message || 'PORT_STRATEGY.md (docs/PORT_STRATEGY.md) is not loaded. Configure location in File Location settings.'}
          </Alert>
        )}
        {data?.port_strategy?.open_link && (
          <Box sx={{ mb: 2 }}>
            <Link href={data.port_strategy.open_link} target="_blank" rel="noreferrer">
              Open Port Strategy File
            </Link>
          </Box>
        )}
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: '#0b1020',
            color: '#dbeafe',
            overflow: 'auto',
            maxHeight: 560,
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap'
          }}
        >
          {data?.port_strategy?.markdown || 'No preview available.'}
        </Box>
      </Paper>
    </Box>
  );
};

export default GeneralSettings;
